/**
 * D64 — Email Health Card.
 * Owner-only Settings tab content showing:
 *  • Primary SMTP live status + last successful send / last failure
 *  • Secondary SMTP fallback status (configured / not configured)
 *  • Failed-email queue size + pending retries
 *  • Test Send button — fires a real probe and reports result
 *
 * Backend: GET /api/settings/email-health
 *          POST /api/settings/email-health/test
 *
 * The "Email Notifications" pill on the Integrations card consumes the same
 * data via getStatusColor() so the system reflects reality, not a hardcoded
 * "Active" lie when SMTP is actively rejecting.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Mail, Send, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

function StatusPill({ tone, label }) {
  // tone: green | amber | red | grey
  const cls = {
    green: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    amber: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    red:   'bg-rose-500/15 text-rose-400 border-rose-500/30',
    grey:  'bg-foreground/[0.05] text-muted-foreground border-border/30',
  }[tone] || 'bg-foreground/[0.05] text-muted-foreground border-border/30';
  const Icon = tone === 'green' ? CheckCircle2 : tone === 'amber' ? AlertTriangle : tone === 'red' ? XCircle : Mail;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

export function deriveSmtpTone(health) {
  if (!health || !health.primary) return { tone: 'grey', label: 'Unknown' };
  const p = health.primary || {};
  const s = health.secondary || {};
  const primaryHealthy = p.last_success && (!p.last_failure || new Date(p.last_success) >= new Date(p.last_failure));
  const secondaryConfigured = !!s.host;
  if (primaryHealthy) return { tone: 'green', label: 'Active' };
  if (secondaryConfigured) return { tone: 'amber', label: 'Degraded — using fallback' };
  return { tone: 'red', label: 'Disabled' };
}

export default function EmailHealthCard({ defaultTestEmail = '' }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testEmail, setTestEmail] = useState(defaultTestEmail);
  const [testing, setTesting] = useState(false);
  const [lastTestResult, setLastTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('stellar_token');
      const r = await axios.get(`${API}/settings/email-health`, { headers: { Authorization: `Bearer ${token}` } });
      setHealth(r.data);
    } catch (err) {
      toast.error('Failed to load email health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTestSend = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setTesting(true);
    setLastTestResult(null);
    try {
      const token = localStorage.getItem('stellar_token');
      const r = await axios.post(`${API}/settings/email-health/test-send`, { to: testEmail }, { headers: { Authorization: `Bearer ${token}` } });
      // D65 P1-4 — Backend returns HTTP 200 with `{ok, queued, reason, error, sender_used}`.
      // We must inspect r.data.ok, NOT just trust the HTTP status. One toast per outcome:
      //   ok && sender_used=primary  → green "Sent via primary"
      //   ok && sender_used=secondary→ amber "Sent via fallback (primary down)"
      //   !ok && queued              → orange "Queued for retry"
      //   !ok && !queued             → red "Failed: <error>"
      const d = r.data || {};
      setLastTestResult(d);
      if (d.ok && d.sender_used === 'primary') {
        toast.success('Sent via primary SMTP');
      } else if (d.ok && d.sender_used === 'secondary') {
        toast.warning('Sent via fallback SMTP — primary is down');
      } else if (d.queued) {
        toast.warning(`Queued for retry — primary down${d.error ? ` (${(d.error || '').slice(0, 80)})` : ''}`);
      } else {
        toast.error(`Send failed: ${(d.error || d.reason || 'unknown').slice(0, 120)}`);
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Send failed';
      setLastTestResult({ ok: false, error: detail });
      toast.error(`Test send failed: ${detail}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card/60 border-border/30">
        <CardContent className="py-8 flex items-center justify-center text-muted-foreground text-[12px]">
          <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Loading email health…
        </CardContent>
      </Card>
    );
  }

  const { tone, label } = deriveSmtpTone(health);
  const p = health?.primary || {};
  const s = health?.secondary || {};

  return (
    <div className="space-y-4" data-testid="email-health-card">
      {/* Overview */}
      <Card className="bg-card/60 border-border/30">
        <CardHeader className="pb-3 flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-gold" /> Email Health
          </CardTitle>
          <div className="flex items-center gap-2">
            <StatusPill tone={tone} label={label} />
            <Button size="sm" variant="ghost" className="h-7" onClick={() => { setRefreshing(true); load(); }} data-testid="email-health-refresh-btn">
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-[12px]">
          {/* Primary */}
          <div className="rounded-lg border border-border/20 p-3 bg-foreground/[0.01]">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-foreground">Primary SMTP</span>
              <span className="text-[10px] text-muted-foreground/50">{p.host}:{p.port}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-muted-foreground">
              <div>User: <span className="text-foreground/80">{p.user || '—'}</span></div>
              <div>From: <span className="text-foreground/80">{p.from_name || '—'}</span></div>
              <div>Last success: <span className="text-foreground/80">{p.last_success ? new Date(p.last_success).toLocaleString() : 'Never'}</span></div>
              <div>Last failure: <span className={p.last_failure ? 'text-rose-400' : 'text-foreground/80'}>{p.last_failure ? new Date(p.last_failure).toLocaleString() : 'None'}</span></div>
            </div>
            {p.last_error && (
              <div className="mt-2 text-[11px] text-rose-400 bg-rose-500/[0.06] border border-rose-500/20 rounded p-2 font-mono whitespace-pre-wrap break-all" data-testid="email-health-primary-error">
                {p.last_error}
              </div>
            )}
          </div>

          {/* Secondary */}
          <div className="rounded-lg border border-border/20 p-3 bg-foreground/[0.01]">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-foreground">Secondary SMTP (Fallback)</span>
              {s.host ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">Configured</Badge> : <Badge className="bg-foreground/[0.05] text-muted-foreground/60 border-border/30 text-[10px]">Not configured</Badge>}
            </div>
            {s.host ? (
              <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                <div>Host: <span className="text-foreground/80">{s.host}:{s.port}</span></div>
                <div>User: <span className="text-foreground/80">{s.user || '—'}</span></div>
                <div>Last success: <span className="text-foreground/80">{s.last_success ? new Date(s.last_success).toLocaleString() : 'Never'}</span></div>
                <div>Last failure: <span className={s.last_failure ? 'text-rose-400' : 'text-foreground/80'}>{s.last_failure ? new Date(s.last_failure).toLocaleString() : 'None'}</span></div>
              </div>
            ) : (
              <p className="text-muted-foreground/60 text-[11px]">Set <code className="text-foreground/80">SMTP_HOST_2 / SMTP_PORT_2 / SMTP_USER_2 / SMTP_PASS_2 / SMTP_FROM_2</code> env vars on prod to enable automatic failover.</p>
            )}
          </div>

          {/* Queue */}
          <div className="rounded-lg border border-border/20 p-3 bg-foreground/[0.01]">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">Failed-email Queue</span>
              <span className="text-[10px] text-muted-foreground/50">Retry worker runs every 60s</span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-[11px]">
              <div className="rounded p-2 bg-foreground/[0.03] text-center">
                <div className="text-muted-foreground/60 text-[10px]">Pending retries</div>
                <div className="text-foreground text-base font-semibold" data-testid="email-health-pending-retries">{health?.pending_retries ?? 0}</div>
              </div>
              <div className="rounded p-2 bg-foreground/[0.03] text-center">
                <div className="text-muted-foreground/60 text-[10px]">Unsent total</div>
                <div className="text-foreground text-base font-semibold">{health?.queued_count ?? 0}</div>
              </div>
              <div className="rounded p-2 bg-foreground/[0.03] text-center">
                <div className="text-muted-foreground/60 text-[10px]">Logged failures</div>
                <div className="text-foreground text-base font-semibold">{health?.failed_total ?? 0}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test send */}
      <Card className="bg-card/60 border-border/30">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Test Send</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[11px] text-muted-foreground/60">Send a real probe email to verify the live pipeline. Uses primary first, then secondary if configured.</p>
          <div className="flex items-center gap-2">
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="recipient@example.com" data-testid="email-health-test-input" />
            <Button size="sm" onClick={handleTestSend} disabled={testing} data-testid="email-health-test-send-btn" className="shrink-0">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Send test
            </Button>
          </div>
          {lastTestResult && (
            <div className={`text-[11px] p-2 rounded border ${lastTestResult.ok ? 'border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-400' : (lastTestResult.queued ? 'border-amber-500/30 bg-amber-500/[0.06] text-amber-400' : 'border-rose-500/30 bg-rose-500/[0.06] text-rose-400')}`} data-testid="email-health-test-result">
              {lastTestResult.ok ? (
                <span>Sent via <strong>{lastTestResult.sender_used || 'primary'}</strong>{lastTestResult.message_id ? ` · ${lastTestResult.message_id}` : ''}</span>
              ) : lastTestResult.queued ? (
                <span>Queued for retry — {lastTestResult.error || lastTestResult.reason || 'primary failed'}</span>
              ) : (
                <span>Failed: {lastTestResult.error || lastTestResult.reason || 'unknown'}</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
